import React, { useEffect, Suspense } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Atom,
  Leaf,
  Users,
  Building2,
  ChevronLeft,
  GraduationCap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
const LinkedInFeed = React.lazy(() => import('../components/ui/LinkedInFeed'));
import Footer from '../components/ui/Footer';
import { Helmet } from 'react-helmet-async';
import {
  MediaController,
  MediaControlBar,
  MediaPlayButton,
  MediaTimeRange,
  MediaVolumeRange,
  MediaMuteButton,
  MediaFullscreenButton,
} from 'media-chrome/react';
import 'media-chrome/menu';

interface Post {
  id: string;
  commentary: string;
  createdAt: number;
  content?: {
    media?: {
      id: string;
      altText?: string;
    };
  };
}

function Home() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [isPaused, setIsPaused] = React.useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = React.useState(true);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isSmallScreen, setIsSmallScreen] = React.useState(false);
  const features = [
    {
      icon: <Atom className="h-8 w-8" />,
      title: t('home.features.ms4materials.title'),
      description: t('home.features.ms4materials.description'),
      link: '/ms4materials',
    },
    {
      icon: <Leaf className="h-8 w-8" />,
      title: t('home.features.ms4nature.title'),
      description: t('home.features.ms4nature.description'),
      link: '/ms4nature',
    },
  ];

  const partners = [
    {
      name: 'Da Vinci Labs',
      logo: '/logos/da_vinci_labs_logo.webp',
      description: "Incubateur et accélérateur dédié à l'innovation deep tech",
      url: 'https://www.davincilabs.eu/',
    },
    {
      name: "Technopôle d'Orléans",
      logo: '/logos/technopole_orleans_logo.webp',
      description:
        'Accompagnement à la création et au devéloppement des entreprises',
      url: 'https://www.tech-orleans.fr/',
    },
    {
      name: 'BPI France',
      logo: '/logos/bpifrance_logo.webp',
      description: "Banque publique d'investissement",
      url: 'https://www.bpifrance.fr/',
    },
    {
      name: 'Féder',
      logo: '/logos/feder_logo.webp',
      description: 'Fond européen de developpement régional',
      url: 'https://www.europe-en-france.gouv.fr/fr/fonds-europeens-2014-2020/fonds-europeen-developpement-regional-feder',
    },
    {
      name: 'France 2030',
      logo: '/logos/france_2030_logo.webp',
      description: "Plus grand plan d'investissement Français ",
      url: 'https://www.france2030.gouv.fr/',
    },
    {
      name: 'Polymeris',
      logo: '/logos/polymeris_logo.webp',
      description: 'Pôle de compétitivité Caoutchouc et Polymères',
      url: 'https://www.polymeris.fr/',
    },
    {
      name: 'Safran',
      logo: '/logos/safran_logo.webp',
      description: 'Safran Group',
      url: 'https://www.safran-group.com/fr',
    },
    {
      name: 'Aquanova',
      logo: '/logos/aquanova_logo.webp',
      description: "Pôle de compétitivité de la filière de l'eau",
      url: 'https://www.poleaquanova.fr/',
    },
  ];

  const founders = [
    {
      name: 'Edouard LETE',
      title: 'CEO & Co-founder',
      image: '/logos/EdouardLETE.png',
      description_en:
        'Engineer and MBA in International marketing, experience in Sales & General Management',
      description_fr:
        "Ingénieur et titulaire d'un MBA en marketing international, expérience en vente et direction générale",
      linkedin: 'https://www.linkedin.com/in/edouard-lete/',
    },
    {
      name: 'Nicolas FROLOFF',
      title: 'CTO & Co-founder',
      image: '/logos/Nicolas-FROLOFF.png',
      description_en:
        'Polytechnician & PhD in Molecular Modeling, experience in Software R&D Management',
      description_fr:
        'Polytechnicien et docteur en modélisation moléculaire, expérience en gestion de R&D logicielle',
      linkedin: 'https://www.linkedin.com/in/nicolas-froloff-3b69144/',
    },
    {
      name: 'Pascal BRAULT',
      title: 'CSO & Co-founder',
      image: '/logos/Pascal-BRAULT.png',
      description_en:
        'PhD in Physics, Director of Research at the CNRS, experience in the Treatment of materials & pollutants.',
      description_fr:
        'Docteur en physique, directeur de recherche au CNRS, expérience dans le traitement des matériaux et des polluants',
      linkedin: 'https://www.linkedin.com/in/pascal-brault-246000260/',
    },
  ];

  // Scroll auto du carrousel des partenaires
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || isPaused) return;

    let frame: number;
    const speed = 0.5;
    const halfWidth = container.scrollWidth / 2;

    const step = () => {
      container.scrollLeft += speed;

      if (container.scrollLeft >= halfWidth) {
        container.scrollLeft = 0;
      }

      frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frame);
  }, [isPaused]);

  const handleScroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollAmount = 500; //

    if (direction === 'left') {
      container.scrollTo({
        left: container.scrollLeft - scrollAmount,
        behavior: 'smooth',
      });
    } else {
      container.scrollTo({
        left: container.scrollLeft + scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  React.useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
    localStorage.setItem('ms4all-home-muted', String(isMuted));
  }, [isMuted]);

  // Play/pause avec barre espace en plein écran
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        document.fullscreenElement === videoRef.current &&
        e.code === 'Space'
      ) {
        e.preventDefault();
        if (videoRef.current) {
          if (videoRef.current.paused) {
            videoRef.current.play();
          } else {
            videoRef.current.pause();
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  React.useEffect(() => {
    const checkScreen = () => setIsSmallScreen(window.innerWidth < 768);
    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('home.metadata.title')}</title>
        <meta name="description" content={t('home.metadata.description')} />
      </Helmet>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-b from-slate-50 to-white pb-20 pt-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-5xl font-extrabold tracking-tight text-gray-900 sm:text-6xl md:text-7xl">
                <span className="mb-2 block">{t('home.hero.title1')}</span>
                <span className="mt-2 block text-[#006AB5]">
                  {t('home.hero.title2')}
                </span>
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-xl font-medium text-gray-600 md:text-2xl">
                {t('home.hero.subtitle')}
              </p>
              <div className="mx-auto mt-8 max-w-md sm:flex sm:justify-center">
                <a
                  href="#focus-areas"
                  className="inline-flex items-center justify-center rounded-md border border-transparent bg-[#006AB5] px-5 py-2 text-base font-medium text-white shadow-lg transition-colors hover:bg-[#005A99]"
                  style={{ textDecoration: 'none' }}
                >
                  {t('home.hero.cta')}{' '}
                  <ChevronRight className="ml-2" size={16} />
                </a>
              </div>
            </div>

            <div className="mx-auto mt-12 max-w-6xl">
              <MediaController
                className="relative aspect-video overflow-hidden rounded-2xl bg-black shadow-2xl ring-1 ring-black/10"
                style={
                  {
                    width: '100%',
                    aspectRatio: '1740 / 940',
                    '--media-primary-color': 'white',
                    '--media-secondary-color': 'transparent',
                    '--media-control-background': 'rgba(0,0,0,0.5)',
                  } as React.CSSProperties
                }
              >
                <video
                  slot="media"
                  ref={videoRef}
                  src="/video-presentation-ms4all.mp4"
                  autoPlay
                  muted={isMuted}
                  loop
                  playsInline
                  preload="metadata"
                  className="h-full w-full object-contain"
                  {...(isMobile
                    ? {
                        onClick: () => {
                          if (videoRef.current) {
                            setIsMuted(false);
                            videoRef.current.muted = false;
                            videoRef.current.play();
                            if (videoRef.current.requestFullscreen) {
                              videoRef.current.requestFullscreen();
                            } else if (
                              (videoRef.current as any).webkitEnterFullscreen
                            ) {
                              (videoRef.current as any).webkitEnterFullscreen();
                            }
                          }
                        },
                      }
                    : {})}
                >
                  <track
                    kind="captions"
                    src="/captions.fr.vtt"
                    srcLang="fr"
                    label="Français"
                  />
                  <track
                    kind="captions"
                    src="/captions.en.vtt"
                    srcLang="en"
                    label="English"
                  />
                </video>
                <MediaControlBar>
                  <MediaPlayButton />
                  <MediaTimeRange />
                  <MediaMuteButton />
                  <MediaVolumeRange />
                  <media-captions-menu hidden anchor="auto" />
                  <media-captions-menu-button />
                  <MediaFullscreenButton />
                </MediaControlBar>
              </MediaController>
            </div>
          </div>
        </div>

        {/* Company Info Section */}
        <div className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <Building2 className="mx-auto h-12 w-12 text-[#006AB5]" />
              <h2 className="mt-4 text-3xl font-bold text-gray-900">
                {t('home.about.title')}
              </h2>
              <p className="mx-auto mt-4 max-w-3xl text-xl text-gray-500">
                {t('home.about.description')}
              </p>
            </div>

            <div className="mt-20 grid grid-cols-1 gap-8 md:grid-cols-3">
              <div className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-[#006AB5] text-white">
                  <Users className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  {t('home.about.earlyAdopters')}
                </h3>
                <p className="mt-2 text-base text-gray-500">
                  {t('home.about.earlyAdoptersDesc')}
                </p>
              </div>
              <div className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-[#006AB5] text-white">
                  <GraduationCap className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  {t('home.about.researchToReal')}
                </h3>
                <p className="mt-2 text-base text-gray-500">
                  {t('home.about.researchToRealDesc')}
                </p>
              </div>
              <div className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-[#006AB5] text-white">
                  <Atom className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  {t('home.about.simulations')}
                </h3>
                <p className="mt-2 text-base text-gray-500">
                  {t('home.about.simulationsDesc')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div id="focus-areas" className="bg-gray-50 py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
              {features.map((feature, index) => (
                <Link
                  key={index}
                  to={feature.link}
                  className="flex flex-col items-center rounded-lg bg-white p-8 text-center shadow-md transition-shadow hover:shadow-lg"
                >
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-[#006AB5]">
                    {feature.icon}
                  </div>
                  <h3 className="mb-2 text-xl font-semibold">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Partners Section */}
        <div className="bg-white py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-16 text-center">
              <h2 className="text-3xl font-bold text-gray-900">
                {t('home.partners.title')}
              </h2>
              <p className="mt-4 text-xl text-gray-500">
                {t('home.partners.description')}
              </p>
            </div>

            {/* Partners Carousel with Controls */}
            <div
              className="relative"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              {/* Navigation Buttons */}
              <button
                onClick={() => handleScroll('left')}
                className="absolute left-0 top-1/2 z-10 -translate-y-1/2 transform rounded-full bg-white/90 p-3 shadow-lg transition-all hover:scale-110 hover:bg-white"
                aria-label="Scroll partners carousel left"
              >
                <ChevronLeft className="h-6 w-6 text-gray-800" />
              </button>

              <button
                onClick={() => handleScroll('right')}
                className="absolute right-0 top-1/2 z-10 -translate-y-1/2 transform rounded-full bg-white/90 p-3 shadow-lg transition-all hover:scale-110 hover:bg-white"
                aria-label="Scroll partners carousel right"
              >
                <ChevronRight className="h-6 w-6 text-gray-800" />
              </button>

              {/* Carousel Container */}
              <div ref={scrollContainerRef} className="overflow-hidden">
                <div className={`flex space-x-9`}>
                  {/* Double the partners array for seamless loop */}
                  {[...partners, ...partners].map((partner, index) => (
                    <div key={index} className="group w-[300px] flex-none">
                      <a
                        href={partner.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                        title={partner.name}
                      >
                        <div className="relative transform overflow-hidden rounded-2xl bg-white shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl">
                          <div className="relative aspect-[3/2] overflow-hidden">
                            <img
                              src={partner.logo}
                              alt={partner.name}
                              className="absolute inset-0 h-full w-full transform object-contain transition-transform duration-700 group-hover:scale-110"
                              width={300}
                              height={200}
                              loading="lazy"
                              decoding="async"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 translate-y-full transform p-6 text-white transition-transform duration-300 group-hover:translate-y-0">
                            <h3 className="mb-2 text-2xl font-bold">
                              {partner.name}
                            </h3>
                            <p className="text-gray-200">
                              {partner.description}
                            </p>
                          </div>
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Co-founders Section */}
        <div className="bg-white py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <Users className="mx-auto h-12 w-12 text-[#006AB5]" />
              <h2 className="mt-4 text-3xl font-bold text-gray-900">
                {t('home.founders.title')}
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-xl text-gray-500">
                {t('home.founders.description')}
              </p>
            </div>

            <div className="mt-20 grid grid-cols-1 gap-12 lg:grid-cols-3">
              {founders.map((founder, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className="flex-shrink-0">
                    <a
                      href={founder.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        className="h-48 w-48 rounded-full object-cover"
                        src={founder.image}
                        alt={founder.name}
                        width={192}
                        height={192}
                        loading="lazy"
                        decoding="async"
                      />
                    </a>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-medium text-gray-900">
                      {founder.name}
                    </h3>
                    <p className="font-medium text-[#006AB5]">
                      {founder.title}
                    </p>
                    {locale === 'fr' ? (
                      <p className="mt-2 text-gray-500">
                        {founder.description_fr}
                      </p>
                    ) : (
                      <p className="mt-2 text-gray-500">
                        {founder.description_en}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Suspense
              fallback={
                <div className="rounded-3xl border border-gray-200 bg-white p-10 text-center shadow-sm">
                  <div className="mx-auto h-8 w-32 animate-pulse rounded-full bg-slate-200" />
                  <p className="mt-6 text-sm text-gray-500">
                    {t('home.linkedinFeed.loading')}
                  </p>
                </div>
              }
            >
              <LinkedInFeed />
            </Suspense>
          </div>
        </div>

        <Footer />
        <style>{`html { scroll-behavior: smooth; }`}</style>
      </div>
    </>
  );
}

export default Home;
